import { createFileRoute } from "@tanstack/react-router";
import ServiceCloseCRM from "@/pages/ServiceCloseCRM";
import {
  buildPageHead,
  breadcrumbSchema,
  serviceSchema,
  faqPageSchema,
} from "@/lib/seo";

const faqs = [
  {
    question: "How fast can you speed up lead response in Close.com?",
    answer:
      "I architect sub-30-second speed-to-lead: instant round-robin routing the moment a lead arrives, automated first-touch SMS, and rep notification with SLA enforcement. Most teams I work with see connect rates climb 40% or more in the first month because no inbound ever sits cold.",
  },
  {
    question:
      "Can you deploy Close.com and Chloe AI without automating bad processes?",
    answer:
      "Yes. I build explicit governance guardrails before any AI cadence goes live: qualification rules, human escalation triggers, and telemetry that flags when Chloe should hand a conversation back. You get the speed of AI-assisted calling without the risk of automating a broken sales motion.",
  },
  {
    question: "What does a Close.com Sales Architect do?",
    answer:
      "A Close.com Sales Architect designs and scales the complete sales pipeline inside Close.com: automated lead routing, power-dialer sequences, pipeline stage governance, custom REST API integrations, AI call summaries, and executive revenue dashboards.",
  },
  {
    question:
      "Can you integrate Close.com with scheduling, forms, and webhooks?",
    answer:
      "Yes. Using Close's native REST API and webhook endpoints, I architect real-time inbound routing, automated multi-touch sequences (SMS + email + tasks), and automatic activity logging from external demo schedulers.",
  },
  {
    question:
      "Do you build custom reporting and pipeline telemetry for Close.com?",
    answer:
      "Absolutely. I engineer custom opportunity leaderboards, rep call-to-close conversion tracking, speed-to-lead analytics, and pipeline velocity dashboards so leadership knows their pipeline health in real time.",
  },
  {
    question: "How much does a Close.com architect cost?",
    answer:
      "Engagements are scoped per project or retainer. A senior CRM architect delivers the full pipeline architecture at a fraction of a US agency rate. Book a free pipeline audit call to receive a custom scope and quote.",
  },
  {
    question: "Can you fix a Close.com setup another freelancer built?",
    answer:
      "Yes. I inherit and rebuild broken pipelines with a documented audit first, mapping every lead, sequence, and webhook before any changes, so nothing breaks during the transition.",
  },
  {
    question: "Do you build automated lead routing in Close.com?",
    answer:
      "Yes. I engineer real-time round-robin, weighted, and skill-based lead assignment by rep timezone, workload, deal size, or language, with instant notification and SLA enforcement so no inbound waits.",
  },
  {
    question: "Can you connect AI call summaries into Close.com?",
    answer:
      "Yes. I wire LLM transcription pipelines to summarize calls into Close CRM notes and automatically update deal health tags, so reps spend time selling instead of writing notes.",
  },
  {
    question: "How do we get started without disrupting live sales operations?",
    answer:
      "Every engagement begins with an audit and staging blueprint. We test all sequences, routing, and integrations in a sandbox environment before rollout, ensuring your active sales operations run 100% uninterrupted.",
  },
];

export const Route = createFileRoute("/close-crm-automation-specialist")({
  head: () =>
    buildPageHead({
      title:
        "Close CRM Automation Specialist & Sales Architect | Marjohn Robillo",
      description:
        "Hire a Close.com Sales Architect. Sub-30-second lead routing, power-dialer cadences, Chloe AI governance guardrails, pipeline stage governance, and executive revenue dashboards built for velocity.",
      path: "/close-crm-automation-specialist",
      keywords:
        "Close CRM specialist, Close.com expert, sales pipeline automation, speed-to-lead automation, Chloe AI guardrails, outbound dialer automation, Close CRM consultant, lead routing workflow, power dialer sequences, Close CRM migration",
      schema: [
        serviceSchema({
          name: "Close.com CRM Pipeline & Sales Architecture",
          description:
            "High-velocity pipeline engineering, automated calling sequences, inbound lead routing, stage governance, and reporting infrastructure in Close.com.",
          path: "/close-crm-automation-specialist",
          serviceType: [
            "Close.com Implementation",
            "Outbound Sequence Automation",
            "Sales Pipeline Architecture",
            "Speed-to-Lead Routing",
            "Custom API & Webhook Integration",
            "Lead Routing & Assignment",
            "Chloe AI Governance Guardrails",
            "Power Dialer Cadence Engineering",
          ],
        }),
        breadcrumbSchema([
          { name: "Home", path: "" },
          {
            name: "Close.com Sales Architecture",
            path: "/close-crm-automation-specialist",
          },
        ]),
        faqPageSchema(faqs),
      ],
    }),
  component: ServiceCloseCRM,
});
